
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { DialogComponent } from '../../../../shared/components/dialog/dialog.component';
import { SharedService } from '../../../../shared/services/shared.service';

type dialogTypes = 'simple' | 'maximizable' | 'draggable' | 'all';

@Component({
  selector: 'app-profile-modal-component',
  standalone: true,
  imports: [
    DialogComponent,
    ButtonComponent,
    ReactiveFormsModule
],
  templateUrl: './profile-modal.component.html',
  styleUrl: './profile-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileModalComponent implements OnInit {
  @Input() isDialogVisible: boolean = false; // Controls dialog visibility
  @Output() onHide = new EventEmitter<void>(); // Emits when modal closes

  public dialogType: dialogTypes = 'simple';
  public userName = signal<string>(''); // Signals for reactive values
  public userEmail = signal<string>('');
  public userId = signal<number>(0);
  public passwordChanged: boolean = false; // Controls dialog visibility

  public isChangePasswordON = signal<boolean>(false);
  public changePasswordForm!: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly _sharedService: SharedService,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.setUserDetailsFromLocalStorage();
    this.initForm();
  }

  private initForm(): void {
    this.changePasswordForm = this.fb.group({
      oldPassword: ['', [Validators.required]],
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8), // Ensure new password is at least 8 characters
        ],
      ],
    });
  }

  public handleDialogClose(): void {
    this.isDialogVisible = false;
    this.onHide.emit();
  }

  private setUserDetailsFromLocalStorage(): void {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      const user = JSON.parse(currentUser);
      this.userName.set(user?.user_profile?.full_name ?? 'Unknown User');
      this.userEmail.set(user?.user_profile?.email ?? 'Unknown Email');
      this.userId.set(user?.user_profile?.id ?? 0);
    }
  }

  openChangePassword() {
    this.isChangePasswordON.set(true);
  }

  onSubmit() {
    const params = {
      accountId: this.userId(),
      oldPassword: this.changePasswordForm.value.oldPassword,
      newPassword: this.changePasswordForm.value.newPassword,
      apiClientName: 'admin-web-client',
    };

    this._sharedService.changePassword(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
      this.isChangePasswordON.set(false);
      this.passwordChanged = true;
      this._cdr.detectChanges();
    });
  }

  get newPassword() {
    return this.changePasswordForm?.get('newPassword');
  }
  get oldPassword() {
    return this.changePasswordForm?.get('oldPassword');
  }
}
